import { readable, Readable } from "svelte/store";
import type { ProductSizesStoreType } from "@admin/svelte/types/ProductSizesStoreTypes";

interface ProductSizeInfo {
  product_type_name: string;
  container_types: [
    {
      container_type: string;
      container_sizes: [string];
      case_sizes: [
        {
          container_size: string;
          case_sizes: [string];
          pack_sizes: [{ case_size: string; pack_sizes: [string] }];
        },
      ];
    },
  ];
}

const makeProductSizesStore = domain => {
  let initial: ProductSizesStoreType = {
    case_sizes: [],
    container_sizes: [],
    container_types: [],
    pack_sizes: [],
  };
  return readable(initial, makeSubscribe(domain));
};

function makeSubscribe(domain) {
  return set => {
    // This won't get executed until the store has
    // its first subscriber. Kick off retrieval.
    fetchData(domain, set);
    // We're not waiting for the response.
    // Return the unsubscribe function which doesn't do
    // do anything here (but is part of the stores protocol).
    return () => {};
  };
}

async function fetchData(domain, set) {
  await fetch("api/" + domain + "/product_sizes")
    .then(res => res.json())
    .then(data => set(mapData(data)));
}

const withBlankDefault = size_data => {
  if (size_data && size_data.length > 0 && size_data[0] !== "") {
    size_data.unshift("");
  }
  return size_data;
};

const mapData = data => {
  const { alpha_sizes, ...nestedProductSizeData } = data;
  const mappedData = {};
  const rawProductSizeData = Object.entries(nestedProductSizeData);

  for (const [detailLevelKey, productSizeInfo] of rawProductSizeData) {
    const productTypeKeyedContainerTypeDataMap = {};
    (productSizeInfo as ProductSizeInfo[]).forEach(containerTypesData => {
      if (detailLevelKey === "container_types") {
        productTypeKeyedContainerTypeDataMap[containerTypesData.product_type_name] =
          containerTypesData.container_types;
      } else {
        const containerTypeKeyedContainerSizeDataMap = {};
        containerTypesData.container_types.forEach(containerSizeData => {
          if (detailLevelKey === "container_sizes") {
            containerTypeKeyedContainerSizeDataMap[containerSizeData.container_type] =
              containerSizeData.container_sizes.map(container_size => {
                return container_size.split(/\s/).join("");
              });
          } else {
            const contSizeToCaseSize = {};
            containerSizeData.case_sizes.forEach(caseSizeData => {
              if (detailLevelKey === "case_sizes") {
                contSizeToCaseSize[caseSizeData.container_size.split(/\s/).join("")] =
                  withBlankDefault(caseSizeData.case_sizes);
              } else if (detailLevelKey === "pack_sizes") {
                const caseSizeToPackSize = {};
                caseSizeData.pack_sizes.forEach(packSizeData => {
                  caseSizeToPackSize[packSizeData.case_size] = withBlankDefault(
                    packSizeData.pack_sizes,
                  );
                });
                contSizeToCaseSize[caseSizeData.container_size.split(/\s/).join("")] =
                  caseSizeToPackSize;
              }
            });
            containerTypeKeyedContainerSizeDataMap[containerSizeData.container_type] =
              contSizeToCaseSize;
          }
        });
        productTypeKeyedContainerTypeDataMap[containerTypesData.product_type_name] =
          containerTypeKeyedContainerSizeDataMap;
      }
    });
    mappedData[detailLevelKey] = productTypeKeyedContainerTypeDataMap;
  }
  return mappedData;
};

export const ProductSizesStore: Readable<ProductSizesStoreType> = makeProductSizesStore("admin");
export const RetailerProductSizesStore: Readable<ProductSizesStoreType> =
  makeProductSizesStore("retailer");
